<template>
  <div class="gg-engines">
    <!-- Section header -->
    <div class="gg-engines__header">
      <h2 class="gg-engines__heading">Engines</h2>
      <button v-if="isAdm" type="button" class="gg-outline-btn" @click="createOpen = true">
        <v-icon class="gg-outline-btn__icon">mdi-plus</v-icon>
        <span class="gg-outline-btn__label">Novo engine</span>
      </button>
    </div>

    <v-alert v-if="error" type="error" dense text class="mb-3">{{ error }}</v-alert>

    <div class="gg-table">
      <div class="gg-table__head">
        <span class="gg-table__col gg-table__col--nome">Nome / Key</span>
        <span class="gg-table__col gg-table__col--endpoint">Engine</span>
        <span class="gg-table__col gg-table__col--endpoint">WebRTC</span>
        <span class="gg-table__col gg-table__col--status">Status</span>
        <span class="gg-table__col gg-table__col--uso">Uso</span>
        <span class="gg-table__col gg-table__col--acoes">Ações</span>
      </div>

      <div v-for="engine in engines" :key="engine.id" class="gg-table__row">
        <span class="gg-table__col gg-table__col--nome" :title="engine.key">{{ engine.nome || engine.key }}</span>

        <span class="gg-table__col gg-table__col--endpoint">
          <template v-if="engine.host">
            <span class="gg-url__chip" :title="endpointOf(engine)">{{ endpointOf(engine) }}</span>
            <button type="button" class="gg-copy-btn" aria-label="Copiar endereço do engine" @click="copy(endpointOf(engine))">
              <v-icon class="gg-copy-btn__icon">mdi-content-copy</v-icon>
            </button>
          </template>
        </span>

        <span class="gg-table__col gg-table__col--endpoint">
          <template v-if="engine.host">
            <span class="gg-url__chip" :title="webrtcOf(engine)">{{ webrtcOf(engine) }}</span>
            <button type="button" class="gg-copy-btn" aria-label="Copiar endereço WebRTC" @click="copy(webrtcOf(engine))">
              <v-icon class="gg-copy-btn__icon">mdi-content-copy</v-icon>
            </button>
          </template>
        </span>

        <span class="gg-table__col gg-table__col--status">
          <span class="gg-status">
            <span class="gg-status__dot" :class="`gg-status__dot--${statusOf(engine).tone}`" />
            <span
              class="gg-status__label"
              :class="`gg-status__label--${statusOf(engine).tone}`"
              :title="statusOf(engine).hint"
            >
              {{ statusOf(engine).label }}
            </span>
          </span>
        </span>

        <span class="gg-table__col gg-table__col--uso">
          <span
            v-if="engine.codProducaoEmUso"
            class="gg-tag"
            :title="`Produção ${engine.codProducaoEmUso} no ar neste engine`"
          >
            Em uso
          </span>
        </span>

        <span class="gg-table__col gg-table__col--acoes">
          <template v-if="isAdm">
            <button type="button" class="gg-icon-btn gg-icon-btn--plain" aria-label="Editar engine" @click="openForm(engine)">
              <v-icon class="gg-icon-btn__icon gg-icon-btn__icon--sm">mdi-pencil-outline</v-icon>
            </button>
            <!-- The API refuses to delete an allocated engine (409 ENGINE_IN_USE), so
                 the control is disabled rather than hidden — hiding it would leave no
                 explanation for why the engine cannot be removed. -->
            <button
              type="button"
              class="gg-icon-btn gg-icon-btn--plain"
              :class="{ 'gg-icon-btn--disabled': !!engine.codProducaoEmUso }"
              :disabled="!!engine.codProducaoEmUso"
              :aria-label="engine.codProducaoEmUso ? 'Engine em uso, não pode ser excluído' : 'Excluir engine'"
              :title="engine.codProducaoEmUso ? 'Engine em uso por uma produção no ar' : 'Excluir engine'"
              @click="confirmDelete(engine)"
            >
              <v-icon class="gg-icon-btn__icon gg-icon-btn__icon--sm">mdi-trash-can-outline</v-icon>
            </button>
          </template>
        </span>
      </div>

      <div v-if="loading && !engines.length" class="gg-table__empty">Carregando…</div>
      <div v-else-if="!engines.length" class="gg-table__empty">Nenhum engine cadastrado neste site</div>
    </div>

    <!-- Create -->
    <GloboGraphicsEngineFormDrawer v-model="createOpen" />

    <!-- Edit: key is the engine's identity and stays fixed. -->
    <v-dialog v-model="form.open" max-width="492" persistent content-class="gg-dialog">
      <div v-if="form.open" class="gg-dialog__card">
        <div class="gg-dialog__header">
          <h2 class="gg-dialog__title">Editar engine key “{{ form.key }}”</h2>
        </div>

        <div class="gg-dialog__body">
          <div class="gg-field-row">
            <div class="gg-field">
              <label class="gg-field__label" for="gg-edit-engine-nome">Nome</label>
              <input
                id="gg-edit-engine-nome"
                v-model="form.nome"
                type="text"
                class="gg-field__input"
                placeholder="São Paulo / SP"
              />
            </div>
            <div class="gg-switch-field">
              <v-switch v-model="form.active" label="Ativo" hide-details dense class="gg-switch mt-0 pt-0" />
            </div>
          </div>

          <div class="gg-field">
            <label class="gg-field__label" for="gg-edit-engine-host">Host</label>
            <input
              id="gg-edit-engine-host"
              v-model="form.host"
              type="text"
              class="gg-field__input"
              placeholder="10.193.150.114"
            />
          </div>

          <div class="gg-field-row">
            <div class="gg-field">
              <label class="gg-field__label" for="gg-edit-engine-port">Porta</label>
              <input
                id="gg-edit-engine-port"
                v-model.number="form.port"
                type="number"
                class="gg-field__input"
                placeholder="6800"
              />
            </div>
            <div class="gg-field">
              <label class="gg-field__label" for="gg-edit-engine-webrtc">WebRTC Porta</label>
              <input
                id="gg-edit-engine-webrtc"
                v-model.number="form.webrtcPort"
                type="number"
                class="gg-field__input"
                placeholder="9092"
              />
            </div>
          </div>
        </div>

        <div class="gg-dialog__footer">
          <button type="button" class="gg-btn gg-btn--text" @click="form.open = false">Cancelar</button>
          <button type="button" class="gg-btn gg-btn--primary" :disabled="!canSave" @click="save">
            {{ saving ? 'Salvando…' : 'Salvar' }}
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Delete confirmation -->
    <v-dialog v-model="deleteDialog.open" max-width="492" persistent content-class="gg-dialog">
      <div v-if="deleteDialog.open" class="gg-dialog__card">
        <div class="gg-dialog__header">
          <h2 class="gg-dialog__title">Excluir engine “{{ deleteDialog.key }}”?</h2>
        </div>

        <div class="gg-dialog__body">
          <p class="gg-dialog__text">Esta ação é irreversível.</p>
        </div>

        <div class="gg-dialog__footer">
          <button type="button" class="gg-btn gg-btn--text" @click="deleteDialog.open = false">Cancelar</button>
          <button type="button" class="gg-btn gg-btn--critical" :disabled="saving" @click="runDelete">
            {{ saving ? 'Excluindo…' : 'Excluir engine' }}
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import GloboGraphicsEngineFormDrawer from "./GloboGraphicsEngineFormDrawer.vue";

// Allocation (codProducaoEmUso) changes whenever a production goes on or off air,
// with no action on this screen — so the table refreshes itself on the same 30s
// beat the rest of the module uses.
const ENGINES_POLL_MS = 30000;

// Falls back to PLAYOUT_WEBRTC_PORT (9092) server-side when the column is null.
const DEFAULT_WEBRTC_PORT = 9092;

export default {
  name: "PlayoutEnginesManager",
  components: { GloboGraphicsEngineFormDrawer },
  props: {
    // Panel stays mounted; (re)load when it becomes active.
    active: { type: Boolean, default: false },
    siteSigla: { type: String, default: "" },
  },
  data() {
    return {
      saving: false,
      pollTimer: null,
      createOpen: false,
      form: { open: false, id: null, key: "", nome: "", host: "", port: null, webrtcPort: null, active: true },
      deleteDialog: { open: false, key: "", id: null },
    };
  },
  computed: {
    ...mapGetters("playout_engines", {
      engines: "getterEngines",
      loading: "getterEnginesLoading",
      error: "getterEnginesError",
    }),
    ...mapGetters("users", ["isAdm"]),
    canSave() {
      return !this.saving;
    },
  },
  mounted() {
    if (this.active) this.startPolling();
  },
  beforeDestroy() {
    this.stopPolling();
  },
  watch: {
    active(on) {
      if (on) this.startPolling();
      else this.stopPolling();
    },
    // Engines are sigla-scoped, so a site change is a different inventory.
    siteSigla() {
      if (this.active) this.startPolling();
    },
  },
  methods: {
    ...mapActions("playout_engines", ["fetchEngines", "updateEngine", "deleteEngine"]),
    startPolling() {
      this.stopPolling();
      this.fetchEngines().catch(() => {});
      this.pollTimer = setInterval(() => {
        // A failed poll must not reject unhandled; the store already exposes the
        // message through getterEnginesError, which the panel renders.
        this.fetchEngines().catch(() => {});
      }, ENGINES_POLL_MS);
    },
    stopPolling() {
      if (!this.pollTimer) return;
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    },

    endpointOf(engine) {
      return engine.host ? `${engine.host}:${engine.port}` : "";
    },
    webrtcOf(engine) {
      if (!engine.host) return "";
      return `ws://${engine.host}:${engine.webrtcPort || DEFAULT_WEBRTC_PORT}/ws`;
    },
    // healthy comes from a TCP probe of the engine's command port, so "disponível"
    // means genuinely reachable — not merely configured. null is "not probed yet"
    // (cold start, or an inactive engine the prober skips), which is not "down".
    statusOf(engine) {
      if (!engine.host || !engine.port) {
        return { label: "não configurado", tone: "muted", hint: "Sem host/porta cadastrados" };
      }
      if (engine.active === false) {
        return { label: "inativo", tone: "muted", hint: "Engine fora do pool" };
      }
      if (engine.healthy === true) {
        return {
          label: "disponível",
          tone: "success",
          hint: engine.lastCheck
            ? `Acessível — ${new Date(engine.lastCheck).toLocaleTimeString()}`
            : "Acessível",
        };
      }
      if (engine.healthy === false) {
        return {
          label: "indisponível",
          tone: "critical",
          hint: engine.lastError ? `Inacessível (${engine.lastError})` : "Inacessível",
        };
      }
      return { label: "não verificado", tone: "muted", hint: "Ainda não verificado" };
    },

    async copy(text) {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        this.$toast?.success?.("Endereço copiado");
      } catch (e) {
        this.$toast?.error?.("Não foi possível copiar o endereço");
      }
    },

    openForm(item) {
      this.form = {
        open: true,
        id: item.id,
        key: item.key,
        // The API defaults nome to key; show it blank rather than echoing the key.
        nome: item.nome === item.key ? "" : (item.nome || ""),
        host: item.host || "",
        port: item.port || null,
        webrtcPort: item.webrtcPort || null,
        active: item.active !== false,
      };
    },
    async save() {
      if (!this.canSave) return;
      this.saving = true;
      try {
        await this.updateEngine({
          id: this.form.id,
          nome: this.form.nome.trim(),
          host: this.form.host.trim(),
          port: this.form.port,
          webrtcPort: this.form.webrtcPort,
          active: this.form.active,
        });
        this.form.open = false;
        this.$toast?.success?.("Engine salvo");
      } catch (e) {
        this.$toast?.error?.(e?.response?.data?.error || "Erro ao salvar engine");
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(item) {
      if (item.codProducaoEmUso) return;
      this.deleteDialog = { open: true, key: item.key, id: item.id };
    },
    async runDelete() {
      this.saving = true;
      try {
        await this.deleteEngine(this.deleteDialog.id);
        this.deleteDialog.open = false;
        this.$toast?.success?.("Engine excluído");
      } catch (e) {
        this.$toast?.error?.(e?.response?.data?.error || "Erro ao excluir engine");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.gg-engines {
  display: flex;
  flex-direction: column;
  gap: var(--sc-space-6);
}

/* Section header */
.gg-engines__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sc-space-4);
}

.gg-engines__heading {
  margin: 0;
  color: var(--sc-gg-text-title);
  font-size: 20px;
  font-weight: var(--sc-weight-semi);
  line-height: 24px;
}

/* "Novo engine" */
.gg-outline-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 72px;
  height: 40px;
  padding: 0 6px;
  background: transparent;
  border: 1px solid var(--sc-gg-accent);
  border-radius: var(--sc-radius-md);
  cursor: pointer;
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background: rgba(var(--sc-gg-accent-rgb), 0.08);
  }

  &:focus-visible {
    outline: 2px solid var(--sc-gg-accent);
    outline-offset: 2px;
  }
}

.gg-outline-btn__icon {
  color: var(--sc-gg-accent) !important;
  font-size: 20px !important;
}

.gg-outline-btn__label {
  padding: 0 var(--sc-space-1);
  color: var(--sc-gg-accent);
  font-size: 14px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.07px;
  white-space: nowrap;
}

/* Table — fixed-width columns spread by space-between, like the site rows. */
.gg-table {
  display: flex;
  flex-direction: column;
}

.gg-table__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--sc-space-4);
  background: var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md);
  // Two classes, so this outranks .gg-table__col's 16/24 body type — which would
  // otherwise make the head 56 tall instead of 48 and push every row down with it.
  .gg-table__col {
    color: var(--sc-gg-text-secondary);
    font-size: 14px;
    font-weight: var(--sc-weight-medium);
    line-height: 16px;
    letter-spacing: 0.07px;
  }
}

// 64 per row = 16 + 32 + 16, with the rule overlaid at the boundary so it does
// not add height (rows sit at 48 / 112 / 176 in the design).
.gg-table__row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 0 var(--sc-space-4);

  & + .gg-table__row::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: var(--sc-gg-border-100);
  }
}

.gg-table__col {
  display: inline-flex;
  align-items: center;
  gap: var(--sc-space-1);
  min-width: 0;
  overflow: hidden;
  color: var(--sc-gg-text-title);
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.08px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.gg-table__col--nome     { flex: 0 0 152px; }
.gg-table__col--endpoint { flex: 0 0 192px; }
.gg-table__col--status   { flex: 0 0 144px; }
.gg-table__col--uso      { flex: 0 0 56px; }

.gg-table__col--acoes {
  flex: 0 0 72px;
  gap: var(--sc-space-2);
}

.gg-table__empty {
  padding: var(--sc-space-4);
  color: var(--sc-gg-text-tertiary);
  font-size: 14px;
}

/* URL chip + copy */
.gg-url__chip {
  overflow: hidden;
  padding: var(--sc-space-1) var(--sc-space-2);
  background: var(--sc-gg-bg);
  border-radius: var(--sc-radius-sm);
  color: var(--sc-gg-text-title);
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.07px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.gg-copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: var(--sc-space-1);
  background: transparent;
  border: none;
  border-radius: var(--sc-radius-pill);
  cursor: pointer;
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover {
    background: rgba(var(--sc-gg-accent-rgb), 0.12);
  }

  &:focus-visible {
    outline: 2px solid var(--sc-gg-accent);
    outline-offset: 1px;
  }
}

.gg-copy-btn__icon {
  color: var(--sc-gg-accent) !important;
  font-size: 16px !important;
}

/* Status dot + label */
.gg-status {
  display: inline-flex;
  align-items: center;
  gap: var(--sc-space-1);
}

.gg-status__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: var(--sc-radius-md);
}

.gg-status__dot--success  { background: var(--sc-gg-success-strong); }
.gg-status__dot--critical { background: var(--sc-gg-error); }
.gg-status__dot--muted    { background: var(--sc-gg-text-tertiary); }

.gg-status__label {
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.08px;
  white-space: nowrap;
}

.gg-status__label--success  { color: var(--sc-gg-success); }
.gg-status__label--critical { color: var(--sc-gg-critical); }
.gg-status__label--muted    { color: var(--sc-gg-text-tertiary); }

/* Icon buttons */
.gg-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 6px;
  background: transparent;
  border: none;
  border-radius: var(--sc-radius-pill);
  cursor: pointer;
  transition: background var(--sc-duration-fast) var(--sc-easing-standard);

  &:hover:not(:disabled) {
    background: rgba(var(--sc-gg-accent-rgb), 0.12);
  }

  &:focus-visible {
    outline: 2px solid var(--sc-gg-accent);
    outline-offset: 2px;
  }
}

.gg-icon-btn__icon {
  color: var(--sc-gg-accent) !important;
  font-size: 20px !important;
}

.gg-icon-btn--disabled {
  cursor: default;

  .gg-icon-btn__icon {
    color: var(--sc-gg-disabled) !important;
  }
}

/* "Em uso" tag */
.gg-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  min-height: 20px;
  padding: 2px var(--sc-space-1);
  background: var(--sc-gg-info-fill);
  border-radius: var(--sc-radius-sm);
  color: var(--sc-gg-accent);
  font-size: 12px;
  font-weight: var(--sc-weight-medium);
  line-height: 16px;
  letter-spacing: 0.5px;
  white-space: nowrap;
}
</style>
